import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { APP_TIME_ZONE } from '~/lib/config';

/** Merge conditional class names, letting later Tailwind utilities win. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * An API timestamp as a short shop-time date, e.g. "12 Aug 2026, 09:41".
 * Undated or unparseable input renders as an em dash rather than "Invalid Date".
 *
 * The zone is `APP_TIME_ZONE`, not the browser's — see the note there. Only the
 * zone is pinned; the locale is still the reader's, so the day and month order
 * follow whatever their machine is set to.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: APP_TIME_ZONE,
  }).format(date);
}

/**
 * A calendar day from the API — `2026-08-01` — as a short readable date.
 *
 * Deliberately zone-free, and the counterpart to `formatDateTime`. These values
 * are days, not instants: the day a quote came back, the day a shipment landed,
 * the day a job went to the floor. There is no moment to convert and no zone to
 * convert it into, so this splits the string and formats the parts. Handing it
 * to `new Date()` would invent a midnight, and formatting *that* anywhere west
 * of UTC would show the previous day.
 *
 * Undated or malformed input renders as an em dash, like its sibling.
 */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '—';

  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return '—';

  const [, year, month, day] = parts;

  // Built as UTC and read back as UTC: the two cancel, so no zone touches the
  // result. `Date.UTC` also rejects nothing, so an impossible day is caught by
  // comparing what came back against what went in.
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.toISOString().slice(0, 10) !== value) return '—';

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

/**
 * The calendar day an instant falls on in shop time, as `YYYY-MM-DD`.
 *
 * `en-CA` is the trick: it is the one widely-supported locale whose short date
 * format *is* ISO order, so this needs no manual zero-padding and no reaching
 * into `formatToParts`. The result sorts and compares as a string, which is the
 * only thing callers want it for.
 *
 * This exists because `date.getDate()` and friends answer in the *browser's*
 * zone. Asking "is this today?" with them would put a 7pm Phoenix message on
 * tomorrow's date for anyone whose laptop is set to UTC.
 */
export function appDayKey(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: APP_TIME_ZONE,
  }).format(date);
}

/** Whether two instants land on the same calendar day in shop time. */
export function isSameAppDay(a: Date, b: Date): boolean {
  return appDayKey(a) === appDayKey(b);
}

/** Today's date in shop time, in the `YYYY-MM-DD` an `<input type="date">` holds. */
export function todayInAppZone(): string {
  return appDayKey(new Date());
}

/**
 * The units a distance is expressed in, largest first, with how many seconds
 * each one is worth. Weeks are deliberately absent: "3 weeks ago" is a unit
 * people have to convert in their heads, and months carry the same information
 * with less arithmetic.
 */
const DISTANCE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * How long ago, in words — "15 minutes ago", "3 hours ago", "2 days ago".
 *
 * Localised by `Intl.RelativeTimeFormat` rather than by a string table of our
 * own: it knows the plural rules of languages that have more than two, and it
 * is the difference between translating this once and translating it never.
 *
 * The largest unit that fits wins, so an hour-old message reads "1 hour ago"
 * rather than "60 minutes ago". Future timestamps are phrased as such rather
 * than clamped — a message dated tomorrow means a clock is wrong somewhere, and
 * saying "just now" would hide it.
 *
 * `now` is a parameter rather than a read of the clock so that a component can
 * subscribe to a ticking one — see `useNow` — and have these lines stay honest
 * on a page that is left open. It defaults to the clock for one-shot callers.
 */
export function formatDistance(value: string | null | undefined, now = Date.now()): string {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const seconds = Math.round((date.getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  for (const [unit, size] of DISTANCE_UNITS) {
    if (Math.abs(seconds) < size) continue;

    // Rounded on the magnitude and re-signed, because Math.round breaks ties
    // towards positive infinity: a plain Math.round(-1.5) is -1, which would
    // call a 45-day-old message "last month" while a 45-day-old appointment is
    // "in 2 months".
    const distance = seconds / size;
    return format.format(Math.sign(distance) * Math.round(Math.abs(distance)), unit);
  }

  // Under a minute. `numeric: 'auto'` renders zero as "now" rather than
  // "in 0 seconds", which is the whole reason for that option.
  return format.format(0, 'second');
}
